<template>
  <q-card class="widget-card">
    <div class="widget-card__header">
      <div class="flex items-center no-wrap">
        <q-icon
          name="sym_r_360"
          size="24px"
          class="q-mr-xs"
          style="margin-top: -2px"
        />
        <div class="widget-card__title">Market Velocity</div>
      </div>
      <div class="widget-card__subtitle">How often the market is changing.</div>
    </div>
    <div class="q-px-md q-pb-sm">
      <q-select
        ref="marketVelocityConditionSelect"
        v-model="selectedCondition"
        :options="conditionOptions"
        color="secondary"
        dense
        @update:model-value="fetchVelocityData"
      />
    </div>
    <div class="widget-card__content">
      <div v-if="loading" class="q-py-md">
        <q-skeleton type="rect" height="80px" class="q-mb-md" />
        <q-skeleton type="rect" height="30px" class="q-mb-xs" />
        <q-skeleton type="rect" height="30px" />
      </div>
      <div v-else>
        <div class="velocity-hero">
          <div class="velocity-hero__icon">
            <q-icon :name="velocityIcon" size="30px" />
          </div>
          <div class="velocity-hero__content">
            <div class="velocity-hero__label">Current Market Velocity</div>
            <div class="velocity-hero__value">{{ velocityStatus }}</div>
            <!--<div class="velocity-hero__metric">
              <span class="metric-number">{{ formatNumber(newListingsWeek, true, 0) }}</span>
              <span class="metric-label">new this week</span>
            </div>-->
          </div>
        </div>

        <div class="velocity-stats q-my-sm">
          <div class="stat-row">
            <div class="stat-row__label">
              Weekly Additions
            </div>
            <div class="stat-row__value">
              {{ weeklyAdditions &lt; 100000 ? formatNumber(weeklyAdditions) : formatNumber(weeklyAdditions, true, 0) }}
            </div>
          </div>
          <!--<div class="stat-row">
            <div class="stat-row__label">
              Weekly Sales
            </div>
            <div class="stat-row__value">
              {{ weeklySales &lt; 100000 ? formatNumber(weeklySales) : formatNumber(weeklySales, true, 0) }}
            </div>
          </div>-->
          <!--<div class="stat-row">
            <div class="stat-row__label">
              Avg. Listing Age
            </div>
            <div class="stat-row__value">{{ avgListingAge }} days</div>
          </div>-->
          <div class="stat-row">
            <div class="stat-row__label">
              Week-over-Week
            </div>
            <div
              class="stat-row__value"
              :class="
                weekOverWeekChange > 0
                  ? 'positive'
                  : weekOverWeekChange == 0
                  ? 'text-text-light'
                  : 'negative'
              "
            >
              {{
                weekOverWeekChange == 0
                  ? "-"
                  : (weekOverWeekChange > 0 ? "+" : "") +
                    weekOverWeekChange +
                    "%"
              }}
            </div>
          </div>
        </div>

        <div class="insight-box">
          <q-icon name="sym_r_campaign" size="20px" class="q-mr-xs" />
          <div class="insight-box__text">
            {{ marketInsight }}
          </div>
        </div>
      </div>
    </div>
  </q-card>
</template>

<script>
import axios from "axios";
import mixins from "@/mixins";

export default {
  name: "MarketVelocityWidget",
  mixins: [mixins],
  data() {
    return {
      loading: true,
      weeklyAdditions: 0,
      weeklySales: 0,
      avgListingAge: 0,
      weekOverWeekChange: 0,
      selectedCondition: "All Vehicles",
      conditionOptions: ["All Vehicles", "New Vehicles", "Used Vehicles"]
    };
  },
  computed: {
    velocityStatus() {
      if (this.weekOverWeekChange > 15) return "Very High";
      if (this.weekOverWeekChange > 5) return "High";
      if (this.weekOverWeekChange > -5) return "Steady";
      if (this.weekOverWeekChange > -15) return "Slowing";
      return "Low";
    },
    velocityIcon() {
      if (this.weekOverWeekChange > 15) return "sym_r_rocket_launch";
      if (this.weekOverWeekChange > 5) return "sym_r_local_fire_department";
      if (this.weekOverWeekChange > -5) return "sym_r_trending_flat";
      return "sym_r_trending_down";
    },
    marketInsight() {
      if (this.weekOverWeekChange > 10) {
        return "Inventory is growing fast, more choices available but act quickly on popular models.";
      } else if (this.weekOverWeekChange > 0) {
        return "Steady flow of incoming inventory. It's a good time to find a perfect match.";
      } else if (this.weekOverWeekChange > -10) {
        return "Inventory is stabilizing. With less inventory incoming, popular vehicles may sell faster.";
      } else {
        return "Inventory is tightening. Consider acting quickly if you find a match.";
      }
    }
  },
  mounted() {
    this.fetchVelocityData();
  },
  methods: {
    getConditionValue(conditionLabel) {
      const conditionMap = {
        "All Vehicles": null,
        "New Vehicles": "New",
        "Used Vehicles": "Used"
      };
      return conditionMap[conditionLabel];
    },
    async fetchVelocityData() {
      this.loading = true;
      try {
        // Get today's date in YYYY-MM-DD format
        const today = new Date();
        const todayStr = today.toISOString().split("T")[0];

        // Get date 7 days ago
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        const weekAgoStr = weekAgo.toISOString().split("T")[0];

        // Get date 14 days ago
        const twoWeeksAgo = new Date();
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
        const twoWeeksAgoStr = twoWeeksAgo.toISOString().split("T")[0];

        const conditionValue = this.getConditionValue(this.selectedCondition);
        const params = { condition: conditionValue };

        // Fetch this week's stats (last 7 days) and previous week's stats (7-14 days ago)
        const [
          thisWeekResponse,
          lastWeekResponse,
          todayResponse
        ] = await Promise.all([
          axios.get(
            "https://api-v2.jumpfeed.com/network/publisher/products/vehicles/nationwide-market-stats",
            {
              params: { ...params, start_date: weekAgoStr, end_date: todayStr }
            }
          ),
          axios.get(
            "https://api-v2.jumpfeed.com/network/publisher/products/vehicles/nationwide-market-stats",
            {
              params: {
                ...params,
                start_date: twoWeeksAgoStr,
                end_date: weekAgoStr
              }
            }
          ),
          axios.get(
            "https://api-v2.jumpfeed.com/network/publisher/products/vehicles/nationwide-market-stats",
            { params: { ...params, date: todayStr } }
          )
        ]);

        const thisWeekData = thisWeekResponse.data.data;
        const lastWeekData = lastWeekResponse.data.data;
        const todayStats = todayResponse.data.data?.find(
          s => s.condition === conditionValue
        );

        if (thisWeekData && thisWeekData.length > 0) {
          // Sum up added_vehicles for all records in the last 7 days
          this.weeklyAdditions = thisWeekData
            .filter(s => s.condition === conditionValue)
            .reduce((sum, stat) => sum + parseInt(stat.added_vehicles), 0);

          // Sum up removed_vehicles for all records in the last 7 days
          this.weeklySales = thisWeekData
            .filter(s => s.condition === conditionValue)
            .reduce(
              (sum, stat) => sum + parseInt(stat.removed_vehicles || 0),
              0
            );

          // Calculate average listing age from today's avg_date_on_lot
          if (todayStats) {
            const avgDateOnLot = new Date(todayStats.avg_date_on_lot);
            const todayDate = new Date(todayStats.date);
            const daysDiff = Math.floor(
              (todayDate - avgDateOnLot) / (1000 * 60 * 60 * 24)
            );
            this.avgListingAge = Math.abs(daysDiff);
          }

          // Calculate week-over-week change
          if (lastWeekData && lastWeekData.length > 0) {
            const lastWeekAdditions = lastWeekData
              .filter(s => s.condition === conditionValue)
              .reduce((sum, stat) => sum + parseInt(stat.added_vehicles), 0);

            if (lastWeekAdditions > 0) {
              this.weekOverWeekChange = Math.round(
                ((this.weeklyAdditions - lastWeekAdditions) /
                  lastWeekAdditions) *
                  100
              );
            } else {
              this.weekOverWeekChange = 100;
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch velocity data:", error);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.velocity-hero {
  display: flex;
  gap: $space-md;
  padding: 12px 0 $space-xs;

  &__icon {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: color(positive);
    color: color(text);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__content {
    flex: 1;
    align-self: center;
  }

  &__label {
    font-size: 0.8rem;
    color: color(text, light);
    letter-spacing: 0.05em;
    font-weight: 600;
  }

  &__value {
    font-size: 1.2rem;
    font-weight: 800;
    color: color(text);
    line-height: 1.2;
    background: color(text);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  &__metric {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin-top: 4px;

    .metric-number {
      font-size: 1.1rem;
      font-weight: 700;
      color: color(text);
    }

    .metric-label {
      font-size: 0.9rem;
      color: color(text, light);
    }
  }
}

.velocity-stats {
  padding: 0;
}

.stat-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $space-sm 0;
  background: color(background, lighter);
  transition: all 0.2s ease;
  border-bottom: 1px solid color(border);

  &:last-child {
    border-bottom: none;
  }

  &__label {
    display: flex;
    align-items: center;
    color: color(text);
    font-weight: 500;

    .q-icon {
      color: color(text, light);
      margin-right: $space-sm;
    }
  }

  &__value {
    font-size: 1rem;
    font-weight: 700;
    color: color(text);

    &.positive {
      color: $positive;
    }

    &.negative {
      color: $negative;
    }
  }
}
</style>
